import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { LoadingSpinner, ToastProvider } from "@gradebook/ui";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NewVersionNotice } from "@/components/NewVersionNotice";
import { Layout } from "@/components/layout/Layout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
// `ProtectedRoute` plus one question: does this school run the gradebook at
// all? Every route below that leads to a grade uses it, so a school live on
// attendance alone cannot be walked into one by a bookmark. See
// `components/auth/GradebookRoute.tsx`.
import { GradebookRoute } from "@/components/auth/GradebookRoute";
import { SchoolSlugGuard } from "@/components/auth/SchoolSlugGuard";
import { useAuthStore } from "@/store/authStore";
import { ROUTES } from "@/lib/routes";
import { lazyPage } from "@/lib/staleDeploy";

// Route components are code-split via React.lazy so each page ships in its own
// chunk and the initial entry bundle stays small. These pages use named exports,
// so map them to the { default } shape React.lazy expects.
//
// `lazyPage` rather than `lazy` directly: every one of these imports names a
// content-hashed chunk that stops existing the moment the Worker is redeployed,
// and a tab open across a deploy would otherwise crash on the first route it
// navigates to. See lib/staleDeploy.ts.
const Login = lazyPage(() =>
  import("@/pages/auth/Login").then((m) => ({ default: m.Login }))
);
const Signup = lazyPage(() =>
  import("@/pages/auth/Signup").then((m) => ({ default: m.Signup }))
);
const NewSchool = lazyPage(() =>
  import("@/pages/auth/NewSchool").then((m) => ({ default: m.NewSchool }))
);
const Dashboard = lazyPage(() =>
  import("@/pages/dashboard/Dashboard").then((m) => ({ default: m.Dashboard }))
);
const Gradebook = lazyPage(() =>
  import("@/pages/gradebook/Gradebook").then((m) => ({ default: m.Gradebook }))
);
const Students = lazyPage(() =>
  import("@/pages/students/Students").then((m) => ({ default: m.Students }))
);
const StudentDetail = lazyPage(() =>
  import("@/pages/students/StudentDetail").then((m) => ({
    default: m.StudentDetail,
  }))
);
const Courses = lazyPage(() =>
  import("@/pages/courses/Courses").then((m) => ({ default: m.Courses }))
);
const CourseDetail = lazyPage(() =>
  import("@/pages/courses/CourseDetail").then((m) => ({
    default: m.CourseDetail,
  }))
);
const Settings = lazyPage(() =>
  import("@/pages/settings/Settings").then((m) => ({ default: m.Settings }))
);
// Its chunk carries Radix Themes' stylesheet and the WorkOS widget, neither of
// which any other page uses — so this one really does have to stay split out.
const SignInSecurity = lazyPage(() =>
  import("@/pages/account/SignInSecurity").then((m) => ({
    default: m.SignInSecurity,
  }))
);
const WhatsNew = lazyPage(() =>
  import("@/pages/account/WhatsNew").then((m) => ({ default: m.WhatsNew }))
);
const AttendanceToday = lazyPage(() =>
  import("@/pages/attendance/AttendanceToday").then((m) => ({ default: m.AttendanceToday }))
);
const AttendanceReports = lazyPage(() =>
  import("@/pages/attendance/AttendanceReports").then((m) => ({
    default: m.AttendanceReports,
  }))
);
const AttendanceOverview = lazyPage(() =>
  import("@/pages/attendance/AttendanceOverview").then((m) => ({
    default: m.AttendanceOverview,
  }))
);
const BulkImport = lazyPage(() =>
  import("@/pages/import/BulkImport").then((m) => ({ default: m.BulkImport }))
);
const BulkStudentIntake = lazyPage(() =>
  import("@/pages/import/BulkStudentIntake").then((m) => ({
    default: m.BulkStudentIntake,
  }))
);
const Calendar = lazyPage(() =>
  import("@/pages/calendar/Calendar").then((m) => ({ default: m.Calendar }))
);
const MyGrades = lazyPage(() =>
  import("@/pages/grades/MyGrades").then((m) => ({ default: m.MyGrades }))
);
const YearEndGrades = lazyPage(() =>
  import("@/pages/grades/YearEndGrades").then((m) => ({
    default: m.YearEndGrades,
  }))
);
const ParentDashboard = lazyPage(() =>
  import("@/pages/parent/ParentDashboard").then((m) => ({
    default: m.ParentDashboard,
  }))
);
const ChildGrades = lazyPage(() =>
  import("@/pages/parent/ChildGrades").then((m) => ({ default: m.ChildGrades }))
);
const ApprovalQueue = lazyPage(() =>
  import("@/pages/admin/ApprovalQueue").then((m) => ({
    default: m.ApprovalQueue,
  }))
);
const GradeChangeRequests = lazyPage(() =>
  import("@/pages/admin/GradeChangeRequests").then((m) => ({
    default: m.GradeChangeRequests,
  }))
);
const People = lazyPage(() =>
  import("@/pages/admin/People").then((m) => ({ default: m.People }))
);
const Parents = lazyPage(() =>
  import("@/pages/admin/Parents").then((m) => ({ default: m.Parents }))
);
const Teachers = lazyPage(() =>
  import("@/pages/admin/Teachers").then((m) => ({ default: m.Teachers }))
);
const Homerooms = lazyPage(() =>
  import("@/pages/attendance/Homerooms").then((m) => ({ default: m.Homerooms }))
);
const Departments = lazyPage(() =>
  import("@/pages/admin/Departments").then((m) => ({ default: m.Departments }))
);
const AcademicYears = lazyPage(() =>
  import("@/pages/admin/AcademicYears").then((m) => ({
    default: m.AcademicYears,
  }))
);
const AcademicYearSetup = lazyPage(() =>
  import("@/pages/admin/AcademicYearSetup").then((m) => ({
    default: m.AcademicYearSetup,
  }))
);
const AuditLog = lazyPage(() =>
  import("@/pages/admin/AuditLog").then((m) => ({ default: m.AuditLog }))
);
const GradePosting = lazyPage(() =>
  import("@/pages/admin/GradePosting").then((m) => ({
    default: m.GradePosting,
  }))
);
const MySubmissions = lazyPage(() =>
  import("@/pages/teacher/MySubmissions").then((m) => ({
    default: m.MySubmissions,
  }))
);
const MyGradeChangeRequests = lazyPage(() =>
  import("@/pages/teacher/MyGradeChangeRequests").then((m) => ({
    default: m.MyGradeChangeRequests,
  }))
);

// Homerooms moved out of the admin section and into attendance. Spelled out
// from `useParams` rather than as a relative `..`, which react-router resolves
// against the route hierarchy — one level up from `admin/homerooms` is the
// `/:schoolSlug` layout, not `/:schoolSlug/admin`, and the difference is the
// kind of thing that only shows up as a 404 in production.
function HomeroomsRedirect() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();

  return <Navigate to={ROUTES.attendanceHomerooms(schoolSlug ?? "")} replace />;
}

/**
 * Where `/:schoolSlug/attendance` lands.
 *
 * The overview is the section's first page, but it is admin-only — a teacher's
 * attendance is the room in front of them, and the API refuses them the
 * school-wide view for the same reason. So the root dispatches on the office
 * held rather than being one page: sending everyone to the overview would
 * bounce every teacher straight back to the dashboard.
 */
function AttendanceHome() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  // The getter, not its result: `getEffectiveRoles()` builds a fresh array, and
  // selecting on that hands zustand a new identity every render.
  const getEffectiveRoles = useAuthStore((state) => state.getEffectiveRoles);
  const slug = schoolSlug ?? "";
  const roles = getEffectiveRoles();

  return (
    <Navigate
      to={roles.includes("admin") ? ROUTES.attendanceOverview(slug) : ROUTES.attendanceToday(slug)}
      replace
    />
  );
}

function RootRedirect() {
  const user = useAuthStore((state) => state.user);

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  return <Navigate to={`/${user.schoolSlug}/dashboard`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      {/* Above the ErrorBoundary so a toast fired just before a crash — and one
          fired by the boundary's own recovery UI — still has somewhere to go. */}
      <ToastProvider>
        {/* Renders nothing until the Worker starts serving a newer build, then
            offers a reload. Inside ToastProvider because that is how it speaks,
            and outside ErrorBoundary so a crashed page still gets the offer —
            a stale bundle is a likely reason for the crash. */}
        <NewVersionNotice />
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner text="Loading…" />}>
            <Routes>
              {/* Public routes */}
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/signup" element={<Signup />} />
              {/* Not behind ProtectedRoute: the page is half of a signed-out
                  flow, and it asks for a sign-in itself when there is no
                  session. Guarding it would bounce someone who arrived here from
                  the signup form straight back to a form they just left. */}
              <Route path="/auth/new-school" element={<NewSchool />} />

              {/* Protected routes with school slug */}
              <Route
                path="/:schoolSlug"
                element={
                  <ProtectedRoute>
                    <SchoolSlugGuard>
                      <Layout />
                    </SchoolSlugGuard>
                  </ProtectedRoute>
                }
              >
                <Route path="dashboard" element={<Dashboard />} />
                <Route
                  path="gradebook"
                  element={
                    <GradebookRoute roles={["teacher", "admin"]}>
                      <Gradebook />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="students"
                  element={
                    <ProtectedRoute roles={["admin", "teacher"]}>
                      <Students />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="students/:id"
                  element={
                    <ProtectedRoute roles={["admin", "teacher"]}>
                      <StudentDetail />
                    </ProtectedRoute>
                  }
                />
                {/* Sections, terms and enrolments are the scaffolding grades
                    hang off, so the catalogue belongs to the module. Terms
                    themselves are created with their year on
                    `admin/academic-years`, which is not gated — a school
                    running attendance alone still has an academic year. */}
                <Route
                  path="courses"
                  element={
                    <GradebookRoute roles={["admin", "teacher"]}>
                      <Courses />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="courses/:id"
                  element={
                    <GradebookRoute roles={["admin", "teacher"]}>
                      <CourseDetail />
                    </GradebookRoute>
                  }
                />
                {/* The section root is a way in, not a page of its own: the
                    office opens attendance on the overview and a teacher on
                    their register, and the same link has to serve both. Also
                    what a bookmark of the old register URL lands on. */}
                <Route
                  path="attendance"
                  element={
                    <ProtectedRoute roles={["admin", "teacher"]}>
                      <AttendanceHome />
                    </ProtectedRoute>
                  }
                />
                {/* Attendance (design 1a). Homeroom-and-day scoped, so it is a
                    sibling of the gradebook rather than a page inside it. */}
                <Route
                  path="attendance/today"
                  element={
                    <ProtectedRoute roles={["admin", "teacher"]}>
                      <AttendanceToday />
                    </ProtectedRoute>
                  }
                />
                {/* Admin-only, and the one place attendance narrows rather than
                    widens: the register a teacher takes is their own room, and
                    this is every child in the school with the name of whoever
                    collected them. The API refuses a teacher for the same
                    reason. */}
                <Route
                  path="attendance/overview"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <AttendanceOverview />
                    </ProtectedRoute>
                  }
                />
                {/* Teachers and the office both: the page narrows a teacher
                    to the homerooms they take the register for, and the API
                    keeps the whole-school report to the office. */}
                <Route
                  path="attendance/reports"
                  element={
                    <ProtectedRoute roles={["admin", "teacher"]}>
                      <AttendanceReports />
                    </ProtectedRoute>
                  }
                />
                {/* Admin-only, unlike the rest of attendance: reading which
                    groups exist is what every teacher's register picker needs,
                    but changing them is school configuration. */}
                <Route
                  path="attendance/homerooms"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <Homerooms />
                    </ProtectedRoute>
                  }
                />
                {/* The page used to live under admin/. Redirect rather than
                    404, so bookmarks and any link already sent to a head of
                    year still land on it. */}
                <Route
                  path="admin/homerooms"
                  element={<HomeroomsRedirect />}
                />
                {/* Assignment due dates and nothing else, so it belongs to the
                    gradebook module: with grades off there is nothing left on
                    the page, and hiding the nav link would still leave a
                    bookmark pointing at an empty calendar. */}
                <Route
                  path="calendar"
                  element={
                    <GradebookRoute
                      roles={["teacher", "admin", "student"]}
                      prefetchCourseScope={false}
                    >
                      <Calendar />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="my-grades"
                  element={
                    <GradebookRoute roles={["student"]}>
                      <MyGrades />
                    </GradebookRoute>
                  }
                />
                {/* The year-end subject rollup (AR-12), one page under three paths.
                    The guards here only keep a role off a path that makes no sense
                    for it; who may read whose record is decided by the API. */}
                <Route
                  path="year-end-grades"
                  element={
                    <GradebookRoute roles={["student", "parent"]}>
                      <YearEndGrades />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="students/:id/year-end-grades"
                  element={
                    <GradebookRoute roles={["admin", "teacher"]}>
                      <YearEndGrades />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="parent/children/:studentId/year-end-grades"
                  element={
                    <GradebookRoute roles={["parent"]}>
                      <YearEndGrades />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="parent"
                  element={
                    <ProtectedRoute roles={["parent"]}>
                      <ParentDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="parent/children/:studentId/grades"
                  element={
                    <GradebookRoute roles={["parent"]}>
                      <ChildGrades />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                {/* No `roles`: changing your own password is not an
                    administrative act, and a student has the same claim on it as
                    an admin. The API decides what the page can actually reach,
                    and it reads the bearer's own account and nothing else. */}
                <Route
                  path="account/security"
                  element={
                    <ProtectedRoute>
                      <SignInSecurity />
                    </ProtectedRoute>
                  }
                />
                {/* No `roles`, for the same reason: every role reads the
                    feed, and the page filters it to what is news to them. */}
                <Route
                  path="whats-new"
                  element={
                    <ProtectedRoute>
                      <WhatsNew />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="import"
                  element={
                    <ProtectedRoute roles={["teacher", "admin"]}>
                      <BulkImport />
                    </ProtectedRoute>
                  }
                />
                {/* Admin-only, unlike the spreadsheet importer beside it: an
                    enrolment packet carries a named child's medical details,
                    which is the same reason the API refuses a teacher here. */}
                <Route
                  path="import/enrolment-forms"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <BulkStudentIntake />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/approvals"
                  element={
                    <GradebookRoute roles={["admin"]}>
                      <ApprovalQueue />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="admin/grade-changes"
                  element={
                    <GradebookRoute roles={["admin"]}>
                      <GradeChangeRequests />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="admin/people"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <People />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/parents"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <Parents />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/teachers"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <Teachers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/departments"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <Departments />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/academic-years"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <AcademicYears />
                    </ProtectedRoute>
                  }
                />
                {/* "new" is matched before ":yearId" so it is never read as an id. */}
                <Route
                  path="admin/academic-years/new"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <AcademicYearSetup />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/academic-years/:yearId"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <AcademicYearSetup />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/audit-log"
                  element={
                    <ProtectedRoute roles={["admin"]}>
                      <AuditLog />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/grade-posting"
                  element={
                    <GradebookRoute roles={["admin"]}>
                      <GradePosting />
                    </GradebookRoute>
                  }
                />
                <Route
                  path="my-submissions"
                  element={
                    <GradebookRoute roles={["teacher"]}>
                      <MySubmissions />
                    </GradebookRoute>
                  }
                />
                {/* Admins can raise a change request too (the API allows it), so this
                    is not teacher-only. */}
                <Route
                  path="my-grade-changes"
                  element={
                    <GradebookRoute roles={["teacher", "admin"]}>
                      <MyGradeChangeRequests />
                    </GradebookRoute>
                  }
                />
              </Route>

              {/* Redirect root to school-specific dashboard */}
              <Route path="/" element={<RootRedirect />} />

              {/* Catch all - redirect to root which handles auth */}
              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
